import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    TextField,
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    Chip,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Security as SecurityIcon,
    Group as GroupIcon,
    Person as PersonIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PermissionSettings = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Dialog states
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
    const [userRoleDialogOpen, setUserRoleDialogOpen] = useState(false);
    
    // Form data
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [roleFormData, setRoleFormData] = useState({
        name: '',
        description: '',
        permissions: []
    });
    const [userRoleFormData, setUserRoleFormData] = useState({
        userId: '',
        roles: []
    });

    const { hasPermission, user, refreshUserPermissions } = useAuth();

    useEffect(() => {
        if (hasPermission('system.manage')) {
            fetchRoles();
            fetchPermissions();
            fetchUsers();
            fetchSystemPermissions();
        }
    }, []);

    // Temporary mock data fallback if APIs are not available
    useEffect(() => {
        const timer = setTimeout(() => {
            if (roles.length === 0 && users.length === 0 && !loading) {
                setRoles([
                    { id: '1', name: 'SuperAdmin', description: 'Full system access' },
                    { id: '2', name: 'Admin', description: 'Administrative access' },
                    { id: '3', name: 'User', description: 'Basic user access' }
                ]);
                setUsers([
                    { id: '1', firstName: 'Super', lastName: 'Admin', email: 'admin@example.com', roles: ['SuperAdmin'] },
                    { id: '2', firstName: 'John', lastName: 'Doe', email: 'john@example.com', roles: ['Admin'] }
                ]);
            }
        }, 3000);

        return () => clearTimeout(timer);
    }, [roles, users, loading]);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/auth/roles');
            setRoles(response.data || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
            
            if (error.response?.status === 404) {
                setError('Roles endpoint not found. Please check your backend configuration.');
            } else if (error.response?.status === 403) {
                setError('Access denied. You may not have permission to view roles.');
            } else {
                const errorMessage = error.response?.data?.message || 
                                   'Failed to fetch roles';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchPermissions = async () => {
        try {
            const response = await api.get('/api/permissions');
            setPermissions(response.data || []);
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setError('Failed to fetch permissions');
        }
    };

    const fetchUsers = async () => {
        try {
            // Use the actual AuthController endpoint for admin users
            const response = await api.get('/api/auth/admin-users');
                // Transform the data to match expected format
                const transformedUsers = (response.data || []).map(user => ({
                    id: user.id || user.Id,
                    firstName: user.firstName || user.FirstName,
                    lastName: user.lastName || user.LastName,
                    email: user.email || user.Email,
                    tenantId: user.tenantId || user.TenantId, // Handle both camelCase and PascalCase
                    roles: [user.role || user.Role] // Convert single role to array, handle both cases
                }));
                setUsers(transformedUsers);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Failed to fetch users - you may not have permission to view admin users');
        }
    };    // Available system permissions - these will be loaded from backend
    const [systemPermissions, setSystemPermissions] = useState([]);
    
    const fetchSystemPermissions = async () => {
        try {
            const response = await api.get('/api/permissions');
            const permissionsData = response.data || [];
            setSystemPermissions(permissionsData.map(p => ({
                id: p.id,
                key: p.name,
                name: p.displayName,
                description: p.description,
                category: p.category
            })));
        } catch (error) {
            console.error('Error fetching system permissions:', error);
            // Fallback to default permissions if API fails
            setSystemPermissions([
                { key: 'system.manage', name: 'System Management', description: 'Full system administration access', category: 'System' },
                { key: 'customers.view', name: 'View Customers', description: 'View customer information', category: 'Customers' },
                { key: 'customers.create', name: 'Create Customers', description: 'Create new customers', category: 'Customers' },
                { key: 'customers.update', name: 'Update Customers', description: 'Edit customer information', category: 'Customers' },
                { key: 'customers.delete', name: 'Delete Customers', description: 'Remove customers', category: 'Customers' },
                { key: 'transactions.view', name: 'View Transactions', description: 'View transaction records', category: 'Transactions' },
                { key: 'transactions.create', name: 'Create Transactions', description: 'Create new transactions', category: 'Transactions' },
                { key: 'transactions.update', name: 'Update Transactions', description: 'Edit transaction records', category: 'Transactions' },
                { key: 'transactions.delete', name: 'Delete Transactions', description: 'Remove transactions', category: 'Transactions' },
                { key: 'passbook.print', name: 'Passbook Printing', description: 'Print passbooks and manage print settings', category: 'Printing' }
            ]);
        }
    };

    const handleCreateRole = () => {
        setSelectedRole(null);
        setRoleFormData({
            name: '',
            description: '',
            permissions: []
        });
        setRoleDialogOpen(true);
    };

    const handleEditRole = async (role) => {
        setSelectedRole(role);
        setLoading(true);
        
        try {
            // Load role permissions from backend
            const permissionsResponse = await api.get(`/api/auth/roles/${role.id}/permissions`);
            const rolePermissions = permissionsResponse.data || [];
            
            const formData = {
                name: role.name || '',
                description: role.description || '',
                permissions: rolePermissions
            };
            setRoleFormData(formData);
            
        } catch (error) {
            console.error('Error loading role permissions:', error);
            // If permissions can't be loaded, start with empty permissions
            const formData = {
                name: role.name || '',
                description: role.description || '',
                permissions: []
            };
            setRoleFormData(formData);
        } finally {
            setLoading(false);
        }
        
        setRoleDialogOpen(true);
    };

    const handleSaveRole = async () => {
        try {
            setLoading(true);
            setError('');

            // Check permissions first
            if (!hasPermission('system.manage')) {
                setError('You do not have permission to manage roles. Required permission: system.manage');
                return;
            }

            // Prepare role data matching exact backend DTO structure
            const roleData = {
                Name: roleFormData.name?.trim() || '',
                Description: roleFormData.description?.trim() || ''
            };

            // Validate required fields before sending
            if (!roleData.Name || roleData.Name.trim() === '') {
                setError('Role name is required');
                return;
            }

            let roleId;
            if (selectedRole) {
                const response = await api.put(`/api/auth/roles/${selectedRole.id}`, roleData);
                roleId = selectedRole.id;
                setSuccess('Role updated successfully');
            } else {
                const response = await api.post('/api/auth/roles', roleData);
                roleId = response.data.id;
                setSuccess('Role created successfully');
            }

            // Save role permissions
            try {
                if (roleFormData.permissions && roleFormData.permissions.length > 0) {
                    await api.post(`/api/auth/roles/${roleId}/permissions`, roleFormData.permissions);
                } else {
                    // Clear all permissions if none selected
                    await api.post(`/api/auth/roles/${roleId}/permissions`, []);
                }
            } catch (permissionError) {
                // Don't fail the whole operation if permissions can't be saved
                // Just show a warning
                setError('Role saved but permissions could not be updated: ' + 
                        (permissionError.response?.data?.message || permissionError.message));
            }

            fetchRoles();
            
            // Refresh current user's permissions if they updated their own role
            try {
                await refreshUserPermissions();
                console.log('User permissions refreshed after role update');
            } catch (refreshError) {
                console.error('Failed to refresh user permissions:', refreshError);
            }
            
            setRoleDialogOpen(false);
        } catch (error) {
            // Handle different error types
            let errorMessage = 'Error saving role';
            
            if (error.response?.status === 400) {
                // Handle validation errors
                if (error.response.data?.errors) {
                    const validationErrors = Object.values(error.response.data.errors).flat();
                    errorMessage = validationErrors.join(', ');
                } else if (error.response.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data?.error) {
                    errorMessage = error.response.data.error;
                } else {
                    errorMessage = 'Invalid data provided: ' + JSON.stringify(error.response.data);
                }
            } else if (error.response?.status === 401) {
                errorMessage = 'Authentication required. Please log in again.';
            } else if (error.response?.status === 403) {
                errorMessage = 'Access denied. You do not have permission to manage roles.';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRole = async (roleId) => {
        if (window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
            try {
                setLoading(true);
                await api.delete(`/api/auth/roles/${roleId}`);
                setSuccess('Role deleted successfully');
                fetchRoles();
            } catch (error) {
                console.error('Error deleting role:', error);
                setError(error.response?.data?.message || 'Error deleting role');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleManageUserRoles = (user) => {
        setSelectedUser(user);
        setUserRoleFormData({
            userId: user.id,
            roles: user.roles || []
        });
        setUserRoleDialogOpen(true);
    };

    const handleSaveUserRoles = async () => {
        try {
            setLoading(true);
            setError('');

            console.log('Selected user:', selectedUser);
            console.log('User role form data:', userRoleFormData);

            // Validate that we have the required data
            if (!selectedUser.tenantId) {
                setError('Missing tenant ID for user. Cannot update user roles.');
                return;
            }

            // Use the existing update admin endpoint with Pascal case properties
            const updateData = {
                FirstName: selectedUser.firstName,
                LastName: selectedUser.lastName,
                Email: selectedUser.email,
                TenantId: selectedUser.tenantId, // Use the actual tenantId from the user data
                Role: userRoleFormData.roles[0] || "User" // Take first role since your backend expects single role
                // Password omitted - don't change password when updating roles
            };
            
            console.log('Sending update data:', updateData);
            console.log('Update URL:', `/api/auth/admin-users/${selectedUser.id}`);

            await api.put(`/api/auth/admin-users/${selectedUser.id}`, updateData);

            setSuccess('User roles updated successfully');
            fetchUsers();
            setUserRoleDialogOpen(false);
        } catch (error) {
            console.error('Error updating user roles:', error);
            console.error('Error response data:', error.response?.data);
            console.error('Error status:', error.response?.status);
            
            // Log specific validation errors for debugging
            if (error.response?.data?.errors) {
                console.error('Validation errors:', error.response.data.errors);
            }
            
            let errorMessage = 'Error updating user roles';
            
            // Handle validation errors specifically
            if (error.response?.data?.errors) {
                const validationErrors = Object.entries(error.response.data.errors)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                    .join('; ');
                errorMessage = `Validation errors: ${validationErrors}`;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.data?.title) {
                errorMessage = error.response.data.title;
            } else if (error.response?.data) {
                errorMessage = JSON.stringify(error.response.data);
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionToggle = (permissionKey) => {
        setRoleFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permissionKey)
                ? prev.permissions.filter(p => p !== permissionKey)
                : [...prev.permissions, permissionKey]
        }));
    };

    const handleRoleToggle = (roleName) => {
        setUserRoleFormData(prev => ({
            ...prev,
            roles: prev.roles.includes(roleName)
                ? prev.roles.filter(r => r !== roleName)
                : [...prev.roles, roleName]
        }));
    };

    if (!hasPermission('system.manage')) {
        return (
            <Container maxWidth="md">
                <Alert severity="error">
                    You don't have permission to access permission settings.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Permission Settings
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Manage system roles, permissions, and user access levels
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}
            </Box>

            <Grid container spacing={3}>
                {/* Roles Management */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            avatar={<GroupIcon />}
                            title="System Roles"
                            action={
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreateRole}
                                >
                                    Add Role
                                </Button>
                            }
                        />
                        <CardContent>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Role Name</TableCell>
                                            <TableCell>Permissions</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {roles.map((role) => (
                                            <TableRow key={role.id}>
                                                <TableCell>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {role.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {role.description}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={`${role.permissions?.length || 0} permissions`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        onClick={() => handleEditRole(role)}
                                                        size="small"
                                                        title="Edit Role"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => handleDeleteRole(role.id)}
                                                        size="small"
                                                        color="error"
                                                        title="Delete Role"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {roles.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography color="textSecondary">
                                                        No roles configured
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* User Roles Management */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            avatar={<PersonIcon />}
                            title="User Roles"
                        />
                        <CardContent>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>User</TableCell>
                                            <TableCell>Current Roles</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map((user) => (
                                            <TableRow key={user.id}>
                                                <TableCell>
                                                    <Typography variant="body1">
                                                        {user.firstName} {user.lastName}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {user.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {user.roles?.map(role => (
                                                        <Chip
                                                            key={role}
                                                            label={role}
                                                            size="small"
                                                            sx={{ mr: 0.5, mb: 0.5 }}
                                                        />
                                                    ))}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        onClick={() => handleManageUserRoles(user)}
                                                        size="small"
                                                        title="Manage Roles"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {users.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography color="textSecondary">
                                                        No users found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Available Permissions Reference */}
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            avatar={<SecurityIcon />}
                            title="Available System Permissions"
                        />
                        <CardContent>
                            {Object.entries(
                                systemPermissions.reduce((groups, permission) => {
                                    const category = permission.category || 'General';
                                    if (!groups[category]) groups[category] = [];
                                    groups[category].push(permission);
                                    return groups;
                                }, {})
                            ).map(([category, permissions]) => (
                                <Box key={category} sx={{ mb: 3 }}>
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        {category}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {permissions.map((permission) => (
                                            <Grid item xs={12} key={permission.key}>
                                                <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        {permission.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        {permission.key}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                                        {permission.description}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    {category !== Object.keys(systemPermissions.reduce((groups, permission) => {
                                        const cat = permission.category || 'General';
                                        if (!groups[cat]) groups[cat] = [];
                                        return groups;
                                    }, {})).slice(-1)[0] && <Divider sx={{ mt: 2 }} />}
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Role Dialog */}
            <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedRole ? 'Edit Role' : 'Create New Role'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Role Name"
                                value={roleFormData.name}
                                onChange={(e) => setRoleFormData(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                value={roleFormData.description}
                                onChange={(e) => setRoleFormData(prev => ({ ...prev, description: e.target.value }))}
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Permissions
                            </Typography>
                            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                {Object.entries(
                                    systemPermissions.reduce((groups, permission) => {
                                        const category = permission.category || 'General';
                                        if (!groups[category]) groups[category] = [];
                                        groups[category].push(permission);
                                        return groups;
                                    }, {})
                                ).map(([category, permissions]) => (
                                    <Box key={category} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                                            {category}
                                        </Typography>
                                        <List sx={{ pl: 2 }}>
                                            {permissions.map((permission) => (
                                                <ListItem key={permission.key} disablePadding>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={roleFormData.permissions.includes(permission.key)}
                                                                onChange={() => handlePermissionToggle(permission.key)}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography variant="body2" fontWeight="bold">
                                                                    {permission.name}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {permission.description}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRoleDialogOpen(false)} startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveRole}
                        variant="contained"
                        disabled={!roleFormData.name || loading}
                        startIcon={<SaveIcon />}
                    >
                        {selectedRole ? 'Update Role' : 'Create Role'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* User Roles Dialog */}
            <Dialog open={userRoleDialogOpen} onClose={() => setUserRoleDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Manage User Roles - {selectedUser?.firstName} {selectedUser?.lastName}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Select the roles to assign to this user
                    </Typography>
                    <List sx={{ mt: 2 }}>
                        {roles.map((role) => (
                            <ListItem key={role.id} disablePadding>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={userRoleFormData.roles.includes(role.name)}
                                            onChange={() => handleRoleToggle(role.name)}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {role.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {role.description}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUserRoleDialogOpen(false)} startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveUserRoles}
                        variant="contained"
                        disabled={loading}
                        startIcon={<SaveIcon />}
                    >
                        Update Roles
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default PermissionSettings;




 